import { useInfiniteQuery } from '@tanstack/react-query'
import { buildQueryString } from '@/utils'
import { IQueryParams } from '@/types'
import settings from '@/settings/public'

type useSearchProps = {
  term: string
  params: IQueryParams
  only?: string | string[]
  options?: Record<string, any>
}

export type SearchResults = {
  content: {
    items: any
    fetchNextPage: () => Promise<any>
    hasNextPage: boolean
    isFetchingNextPage: boolean
    isLoading: boolean
    error: any
    isPlaceholderData: boolean
    isFetching: boolean
  }
  teams: {
    items: any
    fetchNextPage: () => Promise<any>
    hasNextPage: boolean
    isFetchingNextPage: boolean
    isLoading: boolean
    error: any
    isPlaceholderData: boolean
    isFetching: boolean
  }
}

const normalizeTerm = (term: string) => term.trim().toLowerCase()

const getPlaceholderItems = (count: number = 20) => {
  const placeholderItem = (index: number) => ({
    _id: `__placeholder__-${index}`,
    description: {
      original: 'placeholder',
    },
    images: [],
  })

  const items = Array.from({ length: count }, (_, index) => placeholderItem(index))

  return {
    pages: [{ data: { items, pagination: { current: 1, next: 2, last: 1 } } }],
    pageParams: [1],
  }
}

const search = async (
  q: { queryKey: any[]; pageParam?: string | number },
  params: IQueryParams,
  endpoint: string
) => {
  try {
    const { queryKey, pageParam = 0 } = q
    const [_, __, term] = queryKey
    const page = String(pageParam)
    const appCode = settings?.app?.code || ''
    const queryParams = buildQueryString({ params: params, pagination: { page } })
    const normalizedTerm = normalizeTerm(term)

    const response = await fetch(`${settings.api.host}/api/1.0/search/${endpoint}${queryParams ? `?${queryParams}` : ''}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ term: normalizedTerm, ...(appCode ? { appCode } : {}) }),
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch ${endpoint} results`)
    }

    return response.json()
  } catch (error) {
    throw new Error(`Failed to fetch ${endpoint} results`)
  }
}

export function useSearch({ term, params, only, options }: useSearchProps): SearchResults {
  const enableContentSearch = only === undefined || only?.includes('content')
  const enableTeamsSearch = only === undefined || only?.includes('teams')

  const {
    data: contentData,
    fetchNextPage: fetchNextContentPage,
    hasNextPage: hasNextContentPage,
    isFetchingNextPage: isFetchingNextContentPage,
    isLoading: isLoadingContent,
    error: contentError,
    isPlaceholderData: isPlaceholderContentData,
    isFetching: isFetchingContent,
  } = useInfiniteQuery({
    queryKey: ['search', 'content', term],
    queryFn: (q) => search(q, params, 'content'),
    initialPageParam: 1,
    enabled: !!term && enableContentSearch,
    placeholderData: getPlaceholderItems(options?.placeholderItems),
    getNextPageParam: (lastPage: any) => {
      const { next, last } = lastPage?.data?.pagination || {}
      return next < last ? next : null
    },
  })

  const {
    data: teamsData,
    fetchNextPage: fetchNextTeamsPage,
    hasNextPage: hasNextTeamsPage,
    isFetchingNextPage: isFetchingNextTeamsPage,
    isLoading: isLoadingTeams,
    error: teamsError,
    isPlaceholderData: isPlaceholderTeamsData,
    isFetching: isFetchingTeams,
  } = useInfiniteQuery({
    queryKey: ['search', 'teams', term],
    queryFn: (q) => search(q, params, 'teams'),
    initialPageParam: 1,
    enabled: !!term && enableTeamsSearch,
    placeholderData: getPlaceholderItems(),
    getNextPageParam: (lastPage: any) => {
      const { next, last } = lastPage?.data?.pagination || {}
      return next < last ? next : null
    },
  })

  return {
    content: {
      items: contentData?.pages?.flatMap((p: any) => p?.data?.items) ?? [],
      fetchNextPage: fetchNextContentPage,
      hasNextPage: hasNextContentPage,
      isFetchingNextPage: isFetchingNextContentPage,
      isLoading: isLoadingContent,
      error: contentError,
      isPlaceholderData: isPlaceholderContentData,
      isFetching: isFetchingContent,
    },
    teams: {
      items: teamsData?.pages?.flatMap((p: any) => p?.data?.items) ?? [],
      fetchNextPage: fetchNextTeamsPage,
      hasNextPage: hasNextTeamsPage,
      isFetchingNextPage: isFetchingNextTeamsPage,
      isLoading: isLoadingTeams,
      error: teamsError,
      isPlaceholderData: isPlaceholderTeamsData,
      isFetching: isFetchingTeams,
    },
  }
}
